import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWindowSize } from 'react-use';

import { AlertDialog } from '../../../../../shared/components/alert-dialog';
import type { MultiMetricDataMap } from '../../DataProvider/MultiMetricProvider';
import { CodeMirrorEditor } from './CodeMirrorEditor';
import type { HelperError } from './utils/error-collector';
import {
  type DataContext,
  validateHandlebarsTemplate,
} from './utils/handlebars-setup';

type TextContentEditorModalProps = {
  isOpen: boolean;
  onClose: () => void;
  startingText: string;
  onSave: (text: string) => void;
  renderPreview: (text: string) => React.ReactNode;
  renderControls?: (
    text: string,
    onTextChange: (text: string) => void,
    hasLintErrors: boolean
  ) => React.ReactNode;
  validationMultiData?: MultiMetricDataMap;
};

export const TextContentEditorModal = ({
  isOpen,
  onClose,
  startingText,
  onSave,
  renderPreview,
  renderControls,
  validationMultiData,
}: TextContentEditorModalProps) => {
  const [draft, setDraft] = useState(startingText);
  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);
  const [hasLintErrors, setHasLintErrors] = useState(false);
  const { height } = useWindowSize();
  const prevIsOpenRef = useRef(isOpen);

  const editorDataContext = useMemo<DataContext>(
    () => ({
      filters: [],
      multiData: validationMultiData,
    }),
    [validationMultiData]
  );

  const validateForLint = useCallback(
    async (content: string): Promise<HelperError[]> => {
      return validateHandlebarsTemplate(content, editorDataContext);
    },
    [editorDataContext]
  );

  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (!wasOpen && isOpen) {
      setDraft(startingText);
      setIsConfirmCloseOpen(false);
      setHasLintErrors(false);
    }
  }, [isOpen, startingText]);

  const isDirty = draft !== startingText;

  const requestClose = () => {
    if (isDirty) {
      setIsConfirmCloseOpen(true);
      return;
    }
    onClose();
  };

  const handleSave = () => {
    onSave(draft);
    onClose();
  };

  const handleExternalTextChange = (text: string) => {
    setDraft(text);
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={requestClose} size="6xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="text.primary">Edit Text Content</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Flex
              flex="1"
              direction={{ base: 'column', md: 'row' }}
              gap={4}
              height={height < 520 ? '50vh' : '67vh'}
            >
              <Box
                width={{ base: '100%', md: '50%' }}
                height={{ base: '50%', md: '100%' }}
              >
                <Flex flexDirection="column" height="100%">
                  <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Text fontSize="sm" fontWeight={600}>
                      Editor
                    </Text>
                    {renderControls
                      ? renderControls(
                          draft,
                          handleExternalTextChange,
                          hasLintErrors
                        )
                      : null}
                  </Flex>
                  <CodeMirrorEditor
                    initialValue={draft}
                    onChange={setDraft}
                    onLintIssues={setHasLintErrors}
                    validate={validateForLint}
                    dataContext={editorDataContext}
                  />
                </Flex>
              </Box>
              <Box
                width={{ base: '100%', md: '50%' }}
                height={{ base: '50%', md: '100%' }}
              >
                <Box height="100%" overflow="auto">
                  <Box position="sticky" top={0} bg="white">
                    <Text fontSize="sm" fontWeight={600} mb={2}>
                      Preview
                    </Text>
                  </Box>
                  {renderPreview(draft)}
                </Box>
              </Box>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="green" onClick={handleSave}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {isConfirmCloseOpen ? (
        <AlertDialog
          isOpen={true}
          alertType="confirm"
          skipDefaultDisclosure={true}
          chakraAlertProps={{ isCentered: true }}
          onClose={() => setIsConfirmCloseOpen(false)}
          headerText="Discard Changes?"
          bodyContent={
            <span>
              You have unsaved changes. Do you want to continue without saving
              them?
            </span>
          }
          footerAcceptIsDestructive
          footerAcceptText="Discard"
          onAccept={() => {
            setIsConfirmCloseOpen(false);
            onClose();
          }}
        />
      ) : null}
    </>
  );
};
